import React from 'react';
import Card from '../../ui/Card';
import DescriptionPanel from '../../ui/DescriptionPanel';
import { TrendingUpIcon, TrendingDownIcon, AlertTriangleIcon } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';
const AuditOverviewDashboard: React.FC = () => {
  const kpis = [{
    title: 'Audit Score %',
    value: '89.5%',
    change: '+4.2%',
    isPositive: true,
    subtitle: 'Overall average'
  }, {
    title: 'NCR Count',
    value: '215',
    change: '-18',
    isPositive: true,
    subtitle: 'Active NCRs'
  }, {
    title: 'CAPA Closure %',
    value: '85%',
    change: '+7%',
    isPositive: true,
    subtitle: 'Closure rate'
  }];
  const auditTrendData = [{
    month: 'Jan',
    score: 84.2,
    target: 85
  }, {
    month: 'Feb',
    score: 85.5,
    target: 85
  }, {
    month: 'Mar',
    score: 86.8,
    target: 85
  }, {
    month: 'Apr',
    score: 87.2,
    target: 85
  }, {
    month: 'May',
    score: 88.1,
    target: 85
  }, {
    month: 'Jun',
    score: 89.5,
    target: 85
  }];
  const plantAuditScoresData = [{
    plant: 'Manesar',
    score: 87.5,
    target: 85
  }, {
    plant: 'Chennai',
    score: 92.8,
    target: 85
  }, {
    plant: 'Pune',
    score: 88.2,
    target: 85
  }, {
    plant: 'Bangalore',
    score: 89.5,
    target: 85
  }];
  const ncrSeverityHeatmapData = [{
    category: 'Process Control',
    critical: 8,
    major: 15,
    minor: 22
  }, {
    category: 'Quality Systems',
    critical: 5,
    major: 12,
    minor: 18
  }, {
    category: 'Documentation',
    critical: 2,
    major: 8,
    minor: 25
  }, {
    category: 'Safety',
    critical: 12,
    major: 18,
    minor: 15
  }, {
    category: 'Environmental',
    critical: 3,
    major: 10,
    minor: 20
  }, {
    category: 'Maintenance',
    critical: 6,
    major: 14,
    minor: 19
  }];
  const auditSummaryData = [{
    plant: 'Manesar',
    auditType: 'Internal',
    date: '2024-01-15',
    score: 87.5,
    auditor: 'Quality Team A',
    ncrCount: 12,
    criticalNCR: 2,
    majorNCR: 4,
    minorNCR: 6,
    capaOpen: 8,
    capaClosed: 4,
    status: 'Completed',
    nextAudit: '2024-04-15'
  }, {
    plant: 'Chennai',
    auditType: 'External OEM',
    date: '2024-01-20',
    score: 92.8,
    auditor: 'Tata Motors',
    ncrCount: 6,
    criticalNCR: 0,
    majorNCR: 2,
    minorNCR: 4,
    capaOpen: 2,
    capaClosed: 4,
    status: 'Completed',
    nextAudit: '2024-07-20'
  }, {
    plant: 'Pune',
    auditType: 'Internal',
    date: '2024-01-25',
    score: 88.2,
    auditor: 'Quality Team B',
    ncrCount: 10,
    criticalNCR: 1,
    majorNCR: 3,
    minorNCR: 6,
    capaOpen: 6,
    capaClosed: 4,
    status: 'Completed',
    nextAudit: '2024-04-25'
  }, {
    plant: 'Bangalore',
    auditType: 'Certification',
    date: '2024-02-01',
    score: 89.5,
    auditor: 'IATF Auditor',
    ncrCount: 8,
    criticalNCR: 1,
    majorNCR: 2,
    minorNCR: 5,
    capaOpen: 5,
    capaClosed: 3,
    status: 'Completed',
    nextAudit: '2025-02-01'
  }, {
    plant: 'Manesar',
    auditType: 'Safety',
    date: '2024-02-05',
    score: 85.0,
    auditor: 'Safety Team',
    ncrCount: 15,
    criticalNCR: 3,
    majorNCR: 5,
    minorNCR: 7,
    capaOpen: 10,
    capaClosed: 5,
    status: 'In Progress',
    nextAudit: '2024-05-05'
  }, {
    plant: 'Chennai',
    auditType: 'Internal',
    date: '2024-02-10',
    score: 91.2,
    auditor: 'Quality Team A',
    ncrCount: 7,
    criticalNCR: 1,
    majorNCR: 2,
    minorNCR: 4,
    capaOpen: 4,
    capaClosed: 3,
    status: 'Completed',
    nextAudit: '2024-05-10'
  }, {
    plant: 'Pune',
    auditType: 'External OEM',
    date: '2024-02-15',
    score: 90.5,
    auditor: 'Hyundai',
    ncrCount: 5,
    criticalNCR: 0,
    majorNCR: 1,
    minorNCR: 4,
    capaOpen: 2,
    capaClosed: 3,
    status: 'Completed',
    nextAudit: '2024-08-15'
  }, {
    plant: 'Bangalore',
    auditType: 'Internal',
    date: '2024-02-20',
    score: 88.8,
    auditor: 'Quality Team B',
    ncrCount: 9,
    criticalNCR: 1,
    majorNCR: 3,
    minorNCR: 5,
    capaOpen: 6,
    capaClosed: 3,
    status: 'In Progress',
    nextAudit: '2024-05-20'
  }];
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Completed':
        return 'bg-green-100 text-green-700';
      case 'In Progress':
        return 'bg-yellow-100 text-yellow-700';
      case 'Planned':
        return 'bg-blue-100 text-blue-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };
  const getScoreColor = (score: number) => {
    if (score >= 90) return '#10b981';
    if (score >= 85) return '#3b82f6';
    if (score >= 80) return '#f59e0b';
    return '#ef4444';
  };
  return <div data-id="element-1145">
      <DescriptionPanel title="Audit & Compliance Overview" description="Comprehensive view of internal and external audit performance, NCR tracking, and CAPA management across all plants. Monitor compliance health, closure rates, and identify areas requiring immediate attention." insights={['Overall audit score improved to 89.5% - exceeding target of 85%', 'Chennai plant leading with 92.8% audit score across all audit types', 'Active NCR count reduced to 215 from 233 last month - 7.7% improvement', 'CAPA closure rate at 85% - on track to meet 90% target by quarter end']} data-id="element-1146" />
      {/* KPIs */}
      <div className="mb-8" data-id="element-1147">
        <h2 className="text-xl font-bold text-gray-900 mb-6" data-id="element-1148">
          Audit Performance Metrics
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1149">
          {kpis.map((kpi, index) => <Card key={index} className="p-6 bg-gradient-to-br from-white to-gray-50" data-id="element-1150">
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1" data-id="element-1151">
                {kpi.title}
              </div>
              <div className="text-xs text-gray-400 mb-3" data-id="element-1152">{kpi.subtitle}</div>
              <div className="text-4xl font-bold text-gray-900 mb-3" data-id="element-1153">
                {kpi.value}
              </div>
              <div className={`text-sm flex items-center font-semibold ${kpi.isPositive ? 'text-green-600' : 'text-red-600'}`} data-id="element-1154">
                {kpi.isPositive ? <TrendingUpIcon size={16} className="mr-1" data-id="element-1155" /> : <TrendingDownIcon size={16} className="mr-1" data-id="element-1156" />}
                {kpi.change}
              </div>
            </Card>)}
        </div>
      </div>
      {/* Audit Trend Chart and Audit Score by Plant */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8" data-id="element-1157">
        {/* Audit Trend Chart */}
        <Card className="p-6" data-id="element-1158">
          <h3 className="text-lg font-semibold text-gray-800 mb-2" data-id="element-1159">
            Audit Trend Chart
          </h3>
          <p className="text-sm text-gray-600 mb-4" data-id="element-1160">
            Overall audit score performance over the last 6 months
          </p>
          <ResponsiveContainer width="100%" height={350} data-id="element-1161">
            <LineChart data={auditTrendData} data-id="element-1162">
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-1163" />
              <XAxis dataKey="month" tick={{
              fontSize: 12
            }} data-id="element-1164" />
              <YAxis domain={[75, 100]} tick={{
              fontSize: 12
            }} data-id="element-1165" />
              <Tooltip contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px'
            }} data-id="element-1166" />
              <Legend data-id="element-1167" />
              <Line type="monotone" dataKey="score" stroke="#3b82f6" strokeWidth={3} name="Audit Score %" dot={{
              r: 5
            }} data-id="element-1168" />
              <Line type="monotone" dataKey="target" stroke="#10b981" strokeWidth={2} strokeDasharray="5 5" name="Target %" dot={{
              r: 4
            }} data-id="element-1169" />
            </LineChart>
          </ResponsiveContainer>
          <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-1170">
            <div className="grid grid-cols-3 gap-4 text-sm" data-id="element-1171">
              <div className="text-center" data-id="element-1172">
                <div className="text-gray-600" data-id="element-1173">Current Score</div>
                <div className="text-2xl font-bold text-blue-600" data-id="element-1174">89.5%</div>
                <div className="text-xs text-gray-500" data-id="element-1175">June 2024</div>
              </div>
              <div className="text-center" data-id="element-1176">
                <div className="text-gray-600" data-id="element-1177">Improvement</div>
                <div className="text-2xl font-bold text-green-600" data-id="element-1178">+5.3%</div>
                <div className="text-xs text-gray-500" data-id="element-1179">vs Jan 2024</div>
              </div>
              <div className="text-center" data-id="element-1180">
                <div className="text-gray-600" data-id="element-1181">Target</div>
                <div className="text-2xl font-bold text-gray-900" data-id="element-1182">85%</div>
                <div className="text-xs text-gray-500" data-id="element-1183">baseline</div>
              </div>
            </div>
          </div>
        </Card>
        {/* Audit Score by Plant */}
        <Card className="p-6" data-id="element-1184">
          <h3 className="text-lg font-semibold text-gray-800 mb-2" data-id="element-1185">
            Audit Score by Plant
          </h3>
          <p className="text-sm text-gray-600 mb-4" data-id="element-1186">
            Plant-wise audit performance comparison
          </p>
          <ResponsiveContainer width="100%" height={350} data-id="element-1187">
            <BarChart data={plantAuditScoresData} data-id="element-1188">
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-1189" />
              <XAxis dataKey="plant" tick={{
              fontSize: 12
            }} data-id="element-1190" />
              <YAxis domain={[75, 100]} tick={{
              fontSize: 12
            }} data-id="element-1191" />
              <Tooltip contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px'
            }} data-id="element-1192" />
              <Legend data-id="element-1193" />
              <Bar dataKey="score" name="Audit Score %" data-id="element-1194">
                {plantAuditScoresData.map((entry, index) => <Cell key={`cell-${index}`} fill={getScoreColor(entry.score)} data-id="element-1195" />)}
              </Bar>
              <Line type="monotone" dataKey="target" stroke="#10b981" strokeWidth={2} strokeDasharray="5 5" name="Target %" dot={false} data-id="element-1196" />
            </BarChart>
          </ResponsiveContainer>
          <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-1197">
            <div className="grid grid-cols-4 gap-4 text-sm" data-id="element-1198">
              {plantAuditScoresData.map((plant, index) => <div key={index} className="text-center" data-id="element-1199">
                  <div className="text-gray-600" data-id="element-1200">{plant.plant}</div>
                  <div className="text-lg font-bold" style={{
                color: getScoreColor(plant.score)
              }} data-id="element-1201">
                    {plant.score}%
                  </div>
                </div>)}
            </div>
          </div>
        </Card>
      </div>
      {/* NCR Severity Heatmap */}
      <Card className="p-6 mb-8" data-id="element-1202">
        <h3 className="text-lg font-semibold text-gray-800 mb-2" data-id="element-1203">
          NCR Severity Heatmap
        </h3>
        <p className="text-sm text-gray-600 mb-4" data-id="element-1204">
          Non-conformance reports by category and severity level
        </p>
        <ResponsiveContainer width="100%" height={400} data-id="element-1205">
          <BarChart data={ncrSeverityHeatmapData} data-id="element-1206">
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-1207" />
            <XAxis dataKey="category" tick={{
            fontSize: 11
          }} angle={-15} textAnchor="end" height={80} data-id="element-1208" />
            <YAxis tick={{
            fontSize: 12
          }} data-id="element-1209" />
            <Tooltip contentStyle={{
            backgroundColor: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: '8px'
          }} data-id="element-1210" />
            <Legend data-id="element-1211" />
            <Bar dataKey="critical" stackId="a" fill="#ef4444" name="Critical" data-id="element-1212" />
            <Bar dataKey="major" stackId="a" fill="#f59e0b" name="Major" data-id="element-1213" />
            <Bar dataKey="minor" stackId="a" fill="#fbbf24" name="Minor" data-id="element-1214" />
          </BarChart>
        </ResponsiveContainer>
        <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-1215">
          <div className="grid grid-cols-4 gap-4 text-sm" data-id="element-1216">
            <div className="text-center" data-id="element-1217">
              <div className="text-gray-600" data-id="element-1218">Total NCRs</div>
              <div className="text-2xl font-bold text-gray-900" data-id="element-1219">215</div>
              <div className="text-xs text-gray-500" data-id="element-1220">active</div>
            </div>
            <div className="text-center" data-id="element-1221">
              <div className="text-gray-600" data-id="element-1222">Critical</div>
              <div className="text-2xl font-bold text-red-600" data-id="element-1223">36</div>
              <div className="text-xs text-gray-500" data-id="element-1224">16.7% of total</div>
            </div>
            <div className="text-center" data-id="element-1225">
              <div className="text-gray-600" data-id="element-1226">Major</div>
              <div className="text-2xl font-bold text-orange-600" data-id="element-1227">77</div>
              <div className="text-xs text-gray-500" data-id="element-1228">35.8% of total</div>
            </div>
            <div className="text-center" data-id="element-1229">
              <div className="text-gray-600" data-id="element-1230">Minor</div>
              <div className="text-2xl font-bold text-yellow-600" data-id="element-1231">102</div>
              <div className="text-xs text-gray-500" data-id="element-1232">47.4% of total</div>
            </div>
          </div>
        </div>
      </Card>
      {/* Audit Summary Table */}
      <Card className="p-6" data-id="element-1233">
        <h3 className="text-lg font-semibold text-gray-800 mb-4" data-id="element-1234">
          Audit Summary
        </h3>
        <div className="overflow-x-auto" data-id="element-1235">
          <table className="w-full" data-id="element-1236">
            <thead data-id="element-1237">
              <tr className="border-b-2 border-gray-300" data-id="element-1238">
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-1239">
                  Plant
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-1240">
                  Audit Type
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-1241">
                  Date
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-1242">
                  Score %
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-1243">
                  Auditor
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-1244">
                  NCR Count
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-1245">
                  Critical
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-1246">
                  Major
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-1247">
                  Minor
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-1248">
                  CAPA Open
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-1249">
                  CAPA Closed
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-1250">
                  Status
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-1251">
                  Next Audit
                </th>
              </tr>
            </thead>
            <tbody data-id="element-1252">
              {auditSummaryData.map((row, index) => <tr key={index} className="border-b border-gray-100 hover:bg-gray-50" data-id="element-1253">
                  <td className="py-3 px-4 text-sm font-medium text-gray-800" data-id="element-1254">
                    {row.plant}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-700" data-id="element-1255">
                    {row.auditType}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-700" data-id="element-1256">
                    {row.date}
                  </td>
                  <td className="py-3 px-4 text-center" data-id="element-1257">
                    <span className="inline-flex px-2.5 py-1 rounded-full text-xs font-bold" style={{
                  backgroundColor: row.score >= 90 ? '#d1fae5' : row.score >= 85 ? '#dbeafe' : row.score >= 80 ? '#fef3c7' : '#fee2e2',
                  color: row.score >= 90 ? '#065f46' : row.score >= 85 ? '#1e40af' : row.score >= 80 ? '#92400e' : '#991b1b'
                }} data-id="element-1258">
                      {row.score}%
                    </span>
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-700" data-id="element-1259">
                    {row.auditor}
                  </td>
                  <td className="py-3 px-4 text-center text-sm font-semibold text-gray-900" data-id="element-1260">
                    {row.ncrCount}
                  </td>
                  <td className="py-3 px-4 text-center" data-id="element-1261">
                    <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-xs font-bold ${row.criticalNCR > 0 ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-400'}`} data-id="element-1262">
                      {row.criticalNCR}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-center" data-id="element-1263">
                    <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-xs font-bold ${row.majorNCR > 0 ? 'bg-orange-100 text-orange-700' : 'bg-gray-100 text-gray-400'}`} data-id="element-1264">
                      {row.majorNCR}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-center" data-id="element-1265">
                    <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-xs font-bold ${row.minorNCR > 0 ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-400'}`} data-id="element-1266">
                      {row.minorNCR}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-center text-sm text-gray-700" data-id="element-1267">
                    {row.capaOpen}
                  </td>
                  <td className="py-3 px-4 text-center text-sm text-green-600 font-semibold" data-id="element-1268">
                    {row.capaClosed}
                  </td>
                  <td className="py-3 px-4 text-center" data-id="element-1269">
                    <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-bold ${getStatusColor(row.status)}`} data-id="element-1270">
                      {row.status}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-700" data-id="element-1271">
                    {row.nextAudit}
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
        <div className="mt-6 pt-4 border-t border-gray-200" data-id="element-1272">
          <div className="grid grid-cols-6 gap-4 text-sm" data-id="element-1273">
            <div className="text-center" data-id="element-1274">
              <div className="text-gray-600" data-id="element-1275">Total Audits</div>
              <div className="text-xl font-bold text-gray-900" data-id="element-1276">8</div>
              <div className="text-xs text-gray-500" data-id="element-1277">this period</div>
            </div>
            <div className="text-center" data-id="element-1278">
              <div className="text-gray-600" data-id="element-1279">Avg Score</div>
              <div className="text-xl font-bold text-blue-600" data-id="element-1280">89.5%</div>
              <div className="text-xs text-gray-500" data-id="element-1281">overall</div>
            </div>
            <div className="text-center" data-id="element-1282">
              <div className="text-gray-600" data-id="element-1283">Total NCRs</div>
              <div className="text-xl font-bold text-gray-900" data-id="element-1284">72</div>
              <div className="text-xs text-gray-500" data-id="element-1285">identified</div>
            </div>
            <div className="text-center" data-id="element-1286">
              <div className="text-gray-600" data-id="element-1287">Critical NCRs</div>
              <div className="text-xl font-bold text-red-600" data-id="element-1288">9</div>
              <div className="text-xs text-gray-500" data-id="element-1289">requiring action</div>
            </div>
            <div className="text-center" data-id="element-1290">
              <div className="text-gray-600" data-id="element-1291">Open CAPAs</div>
              <div className="text-xl font-bold text-orange-600" data-id="element-1292">43</div>
              <div className="text-xs text-gray-500" data-id="element-1293">in progress</div>
            </div>
            <div className="text-center" data-id="element-1294">
              <div className="text-gray-600" data-id="element-1295">Closed CAPAs</div>
              <div className="text-xl font-bold text-green-600" data-id="element-1296">29</div>
              <div className="text-xs text-gray-500" data-id="element-1297">completed</div>
            </div>
          </div>
        </div>
      </Card>
    </div>;
};
export default AuditOverviewDashboard;